import { Badge, Button, Modal } from '@/components';
import {
  scheduledInstantAPI,
  type SiUnreachableRider,
} from '@/services/api/scheduled-instant';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

/** Back-office driver page — the code's digits are the driver id (MTR0012000 → 12000). */
const backOfficeRiderUrl = (riderCode: string): string | null => {
  const base = process.env.REACT_APP_BACK_OFFICE_URL;
  if (!base) return null;
  const id = parseInt(riderCode.replace(/\D/g, ''), 10);
  if (!Number.isFinite(id)) return null;
  return `${base.replace(/\/+$/, '')}/driver/${id}`;
};

const phaseLabel = (p: string): string =>
  p === 'T2' ? 'T-2' : p === 'T12' ? 'T-12' : p;

/**
 * Drill-down for the "Nomor Tidak Aktif" tile — the riders whose confirmation
 * ping failed to deliver in the selected window. One row per rider, matching
 * the tile's count. Fetches lazily when opened.
 */
export const UnreachableRidersModal: React.FC<{
  open: boolean;
  onClose: () => void;
  startDate: string;
  endDate: string;
  client?: string;
}> = ({ open, onClose, startDate, endDate, client }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<SiUnreachableRider[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await scheduledInstantAPI.listUnreachableRiders({
        start_date: startDate,
        end_date: endDate,
        client: client || undefined,
      });
      setRows(res.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, client]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-phone-off-line"
      iconTone="warning"
      title="Nomor Tidak Aktif"
      subtitle={`Pesan gagal terkirim · ${dayjs(startDate).format(
        'DD MMM'
      )} – ${dayjs(endDate).format('DD MMM YYYY')}`}
      width="xl"
      padded={false}
    >
      {loading && (
        <div className="px-5 py-10 text-center text-sm text-gray-400">
          Memuat…
        </div>
      )}

      {!loading && error && (
        <div className="px-5 py-10 text-center">
          <div className="text-sm text-red-600">{error}</div>
          <Button className="mt-3" variant="white" onClick={load}>
            Coba lagi
          </Button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="px-5 py-10 text-center text-sm text-gray-400">
          Tidak ada nomor gagal terkirim pada periode ini.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="text-left px-4 py-2.5 font-semibold">Rider</th>
                <th className="text-left px-4 py-2.5 font-semibold">No. HP</th>
                <th className="text-left px-4 py-2.5 font-semibold">Klien</th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  Tanggal Slot
                </th>
                <th className="text-left px-4 py-2.5 font-semibold">
                  Ping Gagal
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const url = backOfficeRiderUrl(r.riderCode);
                return (
                  <tr key={r.slotRiderId} className="border-b border-gray-50">
                    <td className="px-4 py-3">
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-accent hover:underline"
                        >
                          {r.riderName || r.riderCode}
                        </a>
                      ) : (
                        <span className="font-medium text-ink">
                          {r.riderName || r.riderCode}
                        </span>
                      )}
                      <div className="text-[11px] text-gray-400 font-mono">
                        {r.riderCode}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-gray-700">
                      {r.riderPhone ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{r.clientName}</td>
                    <td className="px-4 py-3 tabular text-gray-700">
                      {dayjs(r.scheduleDate).format('DD MMM')} ·{' '}
                      {r.windowStartTime}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge tone="amber" size="small">
                        {phaseLabel(r.lastFailedPhase)}
                      </Badge>
                      <span className="text-[11px] text-gray-400 ml-2">
                        {dayjs(r.failedAt).format('DD MMM HH:mm')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};
